/**
 * ProjectCard Component
 * Displays a project with its assigned document types (templates).
 * Expandable to show rich template cards; template management via modal.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
  LayoutDashboard,
  Smartphone,
  GitBranch,
  Database,
  Server,
  FolderOpen,
  FileText,
  Minus,
  Users,
} from 'lucide-react';
import { CardAction } from '@/components/ui';
import type { ProjectConfig, ProjectType } from '@/lib/types';
import type { TemplateMetadata } from '@/services/templatesApi';
import { formatDate } from '@/lib/i18n-utils';

// Project type metadata
const projectTypeInfo: Record<
  ProjectType,
  { icon: React.FC<{ className?: string }>; color: string; label: string }
> = {
  'web-application': { icon: LayoutDashboard, color: 'text-blue-600', label: 'Web Application' },
  'mobile-app': { icon: Smartphone, color: 'text-purple-600', label: 'Mobile App' },
  'api-backend': { icon: GitBranch, color: 'text-emerald-600', label: 'API Backend' },
  'data-platform': { icon: Database, color: 'text-amber-600', label: 'Data Platform' },
  'infrastructure': { icon: Server, color: 'text-slate-600', label: 'Infrastructure' },
  'other': { icon: FolderOpen, color: 'text-rose-600', label: 'Other' },
};

// Output format labels
const OUTPUT_FORMAT_LABELS: Record<string, string> = {
  'meeting-notes': 'templates.formatMeetingNotes',
  'adr': 'templates.formatAdr',
  'solution-design': 'templates.formatSolutionDesign',
  'full': 'templates.formatFull',
  'custom': 'templates.formatCustom',
};

// Color classes helper for template cards
const getTemplateColorClasses = (color?: string) => {
  const colors: Record<string, { bg: string; text: string }> = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
    rose: { bg: 'bg-rose-100', text: 'text-rose-600' },
    indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
    slate: { bg: 'bg-slate-100', text: 'text-slate-600' },
  };
  return colors[color || 'slate'] || colors.slate;
};

interface ProjectCardProps {
  project: ProjectConfig;
  assignedTemplates: TemplateMetadata[];
  onEdit: (project: ProjectConfig) => void;
  onDelete: (id: string) => void;
  onRemoveTemplate: (projectId: string, templateId: string) => void;
  onShare: (project: ProjectConfig) => void;
  isDeleting?: boolean;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  assignedTemplates,
  onEdit,
  onDelete,
  onRemoveTemplate,
  onShare,
  isDeleting = false,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const typeInfo = project.type ? projectTypeInfo[project.type] : projectTypeInfo['other'];
  const IconComponent = typeInfo.icon;

  return (
    <div
      className={`
        bg-white rounded-xl border border-slate-200 shadow-sm
        transition-all duration-200 hover:shadow-md
        ${isDeleting ? 'opacity-50 pointer-events-none' : ''}
      `}
    >
      {/* Header */}
      <div
        className="flex items-center gap-4 p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Project Icon */}
        <div className={`p-2.5 rounded-xl bg-slate-100 ${typeInfo.color}`}>
          <IconComponent className="w-5 h-5" />
        </div>

        {/* Project Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 truncate">{project.projectName}</h3>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span>{typeInfo.label}</span>
            <span className="text-slate-300">|</span>
            <span>
              {assignedTemplates.length} template{assignedTemplates.length === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        {/* Expand/Collapse */}
        <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-slate-100">
          {/* Description */}
          {project.description && (
            <p className="mt-3 text-sm text-slate-600">{project.description}</p>
          )}

          {/* Assigned Templates */}
          <div className="mt-4">
            <h4 className="text-sm font-medium text-slate-700 mb-3">
              {t('templates.assignedTemplates', 'Assigned Templates')}
            </h4>

            {/* Rich template cards */}
            {assignedTemplates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {assignedTemplates.map((template) => {
                  const colorClasses = getTemplateColorClasses(template.color);
                  return (
                    <div
                      key={template.id}
                      className="bg-slate-50 rounded-lg border border-slate-200 p-4 hover:shadow-sm transition-shadow"
                    >
                      {/* Template header */}
                      <div className="flex items-start gap-3 mb-2">
                        <div className={`p-1.5 rounded-lg ${colorClasses.bg}`}>
                          <FileText className={`w-4 h-4 ${colorClasses.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-sm text-slate-900 truncate">{template.name}</h5>
                          <p className="text-xs text-slate-500">
                            {t(OUTPUT_FORMAT_LABELS[template.outputFormat] || 'templates.formatCustom')}
                          </p>
                        </div>
                      </div>

                      {/* Description */}
                      {template.description && (
                        <p className="text-xs text-slate-600 mb-3 line-clamp-2">
                          {template.description}
                        </p>
                      )}

                      {/* Footer with date and actions */}
                      <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                        <span className="text-xs text-slate-400">
                          {template.lastUpdated && formatDate(template.lastUpdated)}
                        </span>
                        <CardAction
                          color="amber"
                          iconOnly
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveTemplate(project.id, template.id);
                          }}
                          title={t('templates.removeFromProject', 'Remove from project')}
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </CardAction>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic py-2">
                {t('templates.noTemplatesAssigned', 'No templates assigned yet')}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
            <CardAction color="indigo" onClick={(e) => { e.stopPropagation(); onShare(project); }}>
              <Users className="w-4 h-4" />
              Share
            </CardAction>
            <CardAction color="default" onClick={(e) => { e.stopPropagation(); onEdit(project); }}>
              <Pencil className="w-4 h-4" />
              Edit
            </CardAction>
            <CardAction color="red" onClick={(e) => { e.stopPropagation(); onDelete(project.id); }} disabled={isDeleting}>
              <Trash2 className="w-4 h-4" />
              Delete
            </CardAction>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCard;
